import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Eye,
  FileImage,
  FileText,
  History,
  Loader2,
  Search,
  Tag,
  Trash2,
  Upload,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  createDocument,
  generateDocumentUploadUrl,
  getDocument,
  listDocuments,
  listProperties,
  removeDocument,
  updateDocument,
  uploadDocumentVersion,
} from "@/services/dataService";
import { formatDate, toDateInputValue } from "@/lib/format";

const CATEGORIES = [
  "lease",
  "insurance",
  "contractor",
  "receipt",
  "tax",
  "photo",
  "other",
];

function formatBytes(bytes) {
  const value = Number(bytes || 0);
  if (!value) return "—";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  if (value < 1024 * 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  return `${(value / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function isImageDocument(document) {
  return String(document?.contentType || "").startsWith("image/");
}

function isPdfDocument(document) {
  return String(document?.contentType || "").includes("pdf");
}

function parseTags(value) {
  return value
    .split(",")
    .map((tag) => tag.trim().toLowerCase())
    .filter(Boolean);
}

const DAY_MS = 24 * 60 * 60 * 1000;

function expiryBadge(document) {
  if (!document.expiresAt) return null;
  const daysLeft = Math.floor((document.expiresAt - Date.now()) / DAY_MS);
  if (daysLeft < 0) {
    return { label: "Expired", variant: "destructive" };
  }
  if (daysLeft <= 30) {
    return { label: `Expires in ${daysLeft}d`, variant: "destructive" };
  }
  return { label: `Expires ${formatDate(document.expiresAt)}`, variant: "outline" };
}

async function uploadFileToStorage(file) {
  const uploadUrl = await generateDocumentUploadUrl();
  const uploadResponse = await fetch(uploadUrl, {
    method: "POST",
    headers: {
      "Content-Type": file.type || "application/octet-stream",
    },
    body: file,
  });
  if (!uploadResponse.ok) {
    throw new Error("Upload failed.");
  }
  const { storageId } = await uploadResponse.json();
  return storageId;
}

export default function Documents() {
  const { currentUser } = useAuth();
  const userId = currentUser?._id;
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [propertyFilter, setPropertyFilter] = useState("all");
  const [tagFilter, setTagFilter] = useState("");

  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    title: "",
    category: "lease",
    propertyId: "",
    notes: "",
    tags: "",
    expiresAt: "",
  });

  const [editingDocument, setEditingDocument] = useState(null);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [historyDocumentId, setHistoryDocumentId] = useState(null);
  const [versionFile, setVersionFile] = useState(null);
  const [editForm, setEditForm] = useState({
    title: "",
    category: "other",
    propertyId: "",
    notes: "",
    tags: "",
    expiresAt: "",
  });

  useEffect(() => {
    const propertyIdParam = searchParams.get("propertyId");
    const categoryParam = searchParams.get("category");
    setCategoryFilter(categoryParam || "all");
    if (!propertyIdParam) {
      setPropertyFilter("all");
      return;
    }
    setPropertyFilter(propertyIdParam);
  }, [searchParams]);

  const propertiesQuery = useQuery({
    queryKey: ["properties", userId],
    queryFn: () => listProperties(userId),
    enabled: Boolean(userId),
  });

  const documentsQuery = useQuery({
    queryKey: ["documents", userId],
    queryFn: () => listDocuments(),
    enabled: Boolean(userId),
  });

  const historyQuery = useQuery({
    queryKey: ["document", userId, historyDocumentId],
    queryFn: () => getDocument(historyDocumentId),
    enabled: Boolean(userId && historyDocumentId),
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!selectedFile) {
        throw new Error("Pick a file first.");
      }
      const storageId = await uploadFileToStorage(selectedFile);

      return createDocument({
        propertyId: uploadForm.propertyId || undefined,
        title: uploadForm.title.trim() || selectedFile.name,
        category: uploadForm.category,
        storageId,
        contentType: selectedFile.type || "application/octet-stream",
        fileSizeBytes: selectedFile.size,
        notes: uploadForm.notes.trim() || undefined,
        tags: uploadForm.tags ? parseTags(uploadForm.tags) : undefined,
        expiresAt: uploadForm.expiresAt
          ? new Date(`${uploadForm.expiresAt}T12:00:00`).getTime()
          : undefined,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["documents", userId] });
      setSelectedFile(null);
      setUploadForm({
        title: "",
        category: "lease",
        propertyId: "",
        notes: "",
        tags: "",
        expiresAt: "",
      });
      toast({ title: "Document stored in your vault" });
    },
  });

  const versionMutation = useMutation({
    mutationFn: async () => {
      if (!versionFile || !historyDocumentId) {
        throw new Error("Pick a file first.");
      }
      const storageId = await uploadFileToStorage(versionFile);
      return uploadDocumentVersion({
        id: historyDocumentId,
        storageId,
        contentType: versionFile.type || "application/octet-stream",
        fileSizeBytes: versionFile.size,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["documents", userId] }),
        queryClient.invalidateQueries({ queryKey: ["document", userId, historyDocumentId] }),
      ]);
      setVersionFile(null);
      toast({ title: "New version uploaded" });
    },
    onError: (error) =>
      toast({ title: "Version upload failed", description: error.message, variant: "destructive" }),
  });

  const updateMutation = useMutation({
    mutationFn: updateDocument,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["documents", userId] });
      setEditingDocument(null);
    },
  });

  const removeMutation = useMutation({
    mutationFn: removeDocument,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["documents", userId] });
    },
  });

  const propertyMap = useMemo(
    () =>
      Object.fromEntries(
        (propertiesQuery.data || []).map((property) => [
          property._id,
          property.name || `${property.address}, ${property.city}`,
        ])
      ),
    [propertiesQuery.data]
  );

  const allTags = useMemo(() => {
    const tags = new Set();
    for (const document of documentsQuery.data || []) {
      for (const tag of document.tags || []) {
        tags.add(tag);
      }
    }
    return [...tags].sort();
  }, [documentsQuery.data]);

  const filteredDocuments = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return (documentsQuery.data || []).filter((document) => {
      if (categoryFilter !== "all" && document.category !== categoryFilter) {
        return false;
      }
      if (propertyFilter !== "all") {
        if (propertyFilter === "portfolio") {
          if (document.propertyId) return false;
        } else if (document.propertyId !== propertyFilter) {
          return false;
        }
      }
      if (tagFilter && !(document.tags || []).includes(tagFilter)) {
        return false;
      }
      if (!term) return true;
      const haystack = [
        document.title,
        document.notes,
        document.category,
        ...(document.tags || []),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(term);
    });
  }, [documentsQuery.data, categoryFilter, propertyFilter, tagFilter, searchTerm]);

  function startEdit(document) {
    setEditingDocument(document);
    setEditForm({
      title: document.title || "",
      category: document.category || "other",
      propertyId: document.propertyId || "",
      notes: document.notes || "",
      tags: (document.tags || []).join(", "),
      expiresAt: document.expiresAt ? toDateInputValue(document.expiresAt) : "",
    });
  }

  return (
    <PageLayout
      title="Documents"
      onRefresh={() => documentsQuery.refetch()}
      isRefreshing={documentsQuery.isFetching}
    >
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Secure Vault</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload leases, contracts, insurance documents, PDFs, and photos. Files are stored in Convex file storage and linked to your account.
            </p>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="document-file">Document file</Label>
                <Input
                  id="document-file"
                  type="file"
                  onChange={(event) => {
                    const file = event.target.files?.[0] || null;
                    setSelectedFile(file);
                    if (file) {
                      setUploadForm((prev) => ({
                        ...prev,
                        title: prev.title || file.name,
                      }));
                    }
                  }}
                />
              </div>
              <div className="space-y-1">
                <Label>Title</Label>
                <Input
                  value={uploadForm.title}
                  onChange={(event) =>
                    setUploadForm((prev) => ({ ...prev, title: event.target.value }))
                  }
                  placeholder="Lease - 3441 Dunran"
                />
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-3">
              <div className="space-y-1">
                <Label>Category</Label>
                <select
                  value={uploadForm.category}
                  onChange={(event) =>
                    setUploadForm((prev) => ({ ...prev, category: event.target.value }))
                  }
                  className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                >
                  {CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Property</Label>
                <select
                  value={uploadForm.propertyId}
                  onChange={(event) =>
                    setUploadForm((prev) => ({ ...prev, propertyId: event.target.value }))
                  }
                  className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                >
                  <option value="">Portfolio-level</option>
                  {(propertiesQuery.data || []).map((property) => (
                    <option key={property._id} value={property._id}>
                      {property.name || property.address}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Notes</Label>
                <Input
                  value={uploadForm.notes}
                  onChange={(event) =>
                    setUploadForm((prev) => ({ ...prev, notes: event.target.value }))
                  }
                  placeholder="Optional notes"
                />
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <Label>Tags (comma-separated)</Label>
                <Input
                  value={uploadForm.tags}
                  onChange={(event) =>
                    setUploadForm((prev) => ({ ...prev, tags: event.target.value }))
                  }
                  placeholder="lease, 2026, signed"
                />
              </div>
              <div className="space-y-1">
                <Label>Expiration date (optional — adds calendar reminders)</Label>
                <Input
                  type="date"
                  value={uploadForm.expiresAt}
                  onChange={(event) =>
                    setUploadForm((prev) => ({ ...prev, expiresAt: event.target.value }))
                  }
                />
              </div>
            </div>

            <Button
              onClick={() => uploadMutation.mutate()}
              disabled={!selectedFile || uploadMutation.isPending}
              className="gap-2"
            >
              {uploadMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Upload document
                </>
              )}
            </Button>

            {uploadMutation.error ? (
              <p className="text-sm text-red-600">{uploadMutation.error.message}</p>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Saved Documents</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 md:grid-cols-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  className="pl-9"
                  placeholder="Search documents"
                />
              </div>
              <select
                value={categoryFilter}
                onChange={(event) => {
                  const value = event.target.value;
                  setCategoryFilter(value);
                  const nextParams = new URLSearchParams(searchParams);
                  if (value === "all") {
                    nextParams.delete("category");
                  } else {
                    nextParams.set("category", value);
                  }
                  setSearchParams(nextParams, { replace: true });
                }}
                className="h-10 rounded-md border bg-background px-3 text-sm"
              >
                <option value="all">All categories</option>
                {CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
              <select
                value={propertyFilter}
                onChange={(event) => {
                  const value = event.target.value;
                  setPropertyFilter(value);
                  const nextParams = new URLSearchParams(searchParams);
                  if (value === "all") {
                    nextParams.delete("propertyId");
                    setSearchParams(nextParams, { replace: true });
                  } else {
                    nextParams.set("propertyId", value);
                    setSearchParams(nextParams, { replace: true });
                  }
                }}
                className="h-10 rounded-md border bg-background px-3 text-sm"
              >
                <option value="all">All properties</option>
                <option value="portfolio">Portfolio-level</option>
                {(propertiesQuery.data || []).map((property) => (
                  <option key={property._id} value={property._id}>
                    {property.name || property.address}
                  </option>
                ))}
              </select>
            </div>

            {allTags.length ? (
              <div className="flex flex-wrap items-center gap-1.5">
                <Tag className="h-3.5 w-3.5 text-muted-foreground" />
                {allTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => setTagFilter(tagFilter === tag ? "" : tag)}
                    className={`rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
                      tagFilter === tag
                        ? "border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                        : "text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    #{tag}
                  </button>
                ))}
              </div>
            ) : null}

            <div className="grid gap-3">
              {filteredDocuments.map((document) => (
                <div
                  key={document._id}
                  className="flex flex-col gap-2 rounded-md border p-3 md:flex-row md:items-center md:justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-14 w-14 overflow-hidden rounded-md border bg-muted flex items-center justify-center">
                      {isImageDocument(document) && document.downloadURL ? (
                        <img
                          src={document.downloadURL}
                          alt={document.title}
                          className="h-full w-full object-cover"
                          loading="lazy"
                        />
                      ) : isPdfDocument(document) ? (
                        <span className="text-xs font-semibold text-muted-foreground">PDF</span>
                      ) : (
                        <FileImage className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                    <div className="space-y-1">
                      <p className="font-medium flex items-center gap-2">
                        <FileText className="h-4 w-4 text-primary" />
                        {document.title}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {propertyMap[document.propertyId] || "Portfolio-level"} • {formatDate(document.createdAt)} • {formatBytes(document.fileSizeBytes)}
                        {document.version > 1 ? ` • v${document.version}` : ""}
                      </p>
                      {document.notes ? (
                        <p className="text-xs text-muted-foreground">{document.notes}</p>
                      ) : null}
                      {(document.tags || []).length ? (
                        <div className="flex flex-wrap gap-1">
                          {document.tags.map((tag) => (
                            <button
                              key={tag}
                              type="button"
                              onClick={() => setTagFilter(tagFilter === tag ? "" : tag)}
                              className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-emerald-500/10 hover:text-emerald-700 dark:hover:text-emerald-300"
                            >
                              #{tag}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {(() => {
                      const expiry = expiryBadge(document);
                      return expiry ? <Badge variant={expiry.variant}>{expiry.label}</Badge> : null;
                    })()}
                    <Badge variant="outline">{document.category}</Badge>
                    <Button size="sm" variant="outline" onClick={() => setPreviewDocument(document)}>
                      <Eye className="h-4 w-4 mr-1" />
                      Preview
                    </Button>
                    {document.downloadURL ? (
                      <Button asChild size="sm" variant="outline">
                        <a href={document.downloadURL} target="_blank" rel="noreferrer">
                          Open
                        </a>
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setHistoryDocumentId(document._id)}
                    >
                      <History className="h-4 w-4 mr-1" />
                      Versions
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => startEdit(document)}>
                      Edit
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => {
                        if (
                          window.confirm(
                            `Delete "${document.title}" and all of its versions? This cannot be undone.`
                          )
                        ) {
                          removeMutation.mutate(document._id);
                        }
                      }}
                      disabled={removeMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              ))}

              {filteredDocuments.length === 0 ? (
                <p className="text-center text-sm text-muted-foreground py-8">
                  No documents found.
                </p>
              ) : null}
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={Boolean(previewDocument)} onOpenChange={(open) => !open && setPreviewDocument(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>{previewDocument?.title || "Document preview"}</DialogTitle>
          </DialogHeader>

          {previewDocument ? (
            <div className="space-y-3">
              {isImageDocument(previewDocument) && previewDocument.downloadURL ? (
                <img
                  src={previewDocument.downloadURL}
                  alt={previewDocument.title}
                  className="max-h-[70vh] w-full rounded-md border object-contain bg-muted"
                />
              ) : isPdfDocument(previewDocument) && previewDocument.downloadURL ? (
                <iframe
                  src={previewDocument.downloadURL}
                  title={previewDocument.title}
                  className="h-[70vh] w-full rounded-md border bg-muted"
                />
              ) : (
                <div className="rounded-md border p-6 text-sm text-muted-foreground">
                  Preview is not available for this file type. Use Open to download/view.
                </div>
              )}
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDocument(null)}>
              Close
            </Button>
            {previewDocument?.downloadURL ? (
              <Button asChild>
                <a href={previewDocument.downloadURL} target="_blank" rel="noreferrer">
                  Open in new tab
                </a>
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editingDocument)} onOpenChange={(open) => !open && setEditingDocument(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit document</DialogTitle>
          </DialogHeader>

          <div className="grid gap-3">
            <div className="space-y-1">
              <Label>Title</Label>
              <Input
                value={editForm.title}
                onChange={(event) => setEditForm((prev) => ({ ...prev, title: event.target.value }))}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Category</Label>
                <select
                  value={editForm.category}
                  onChange={(event) =>
                    setEditForm((prev) => ({ ...prev, category: event.target.value }))
                  }
                  className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                >
                  {CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label>Property</Label>
                <select
                  value={editForm.propertyId}
                  onChange={(event) =>
                    setEditForm((prev) => ({ ...prev, propertyId: event.target.value }))
                  }
                  className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                >
                  <option value="">Portfolio-level</option>
                  {(propertiesQuery.data || []).map((property) => (
                    <option key={property._id} value={property._id}>
                      {property.name || property.address}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="space-y-1">
              <Label>Notes</Label>
              <Input
                value={editForm.notes}
                onChange={(event) => setEditForm((prev) => ({ ...prev, notes: event.target.value }))}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Tags (comma-separated)</Label>
                <Input
                  value={editForm.tags}
                  onChange={(event) =>
                    setEditForm((prev) => ({ ...prev, tags: event.target.value }))
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>Expiration date</Label>
                <Input
                  type="date"
                  value={editForm.expiresAt}
                  onChange={(event) =>
                    setEditForm((prev) => ({ ...prev, expiresAt: event.target.value }))
                  }
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingDocument(null)}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                updateMutation.mutate({
                  id: editingDocument._id,
                  title: editForm.title.trim(),
                  category: editForm.category,
                  notes: editForm.notes.trim() || undefined,
                  propertyId: editForm.propertyId || undefined,
                  clearPropertyId: editForm.propertyId === "",
                  tags: parseTags(editForm.tags),
                  expiresAt: editForm.expiresAt
                    ? new Date(`${editForm.expiresAt}T12:00:00`).getTime()
                    : undefined,
                  clearExpiresAt: editForm.expiresAt === "",
                })
              }
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(historyDocumentId)}
        onOpenChange={(open) => {
          if (!open) {
            setHistoryDocumentId(null);
            setVersionFile(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Version history{historyQuery.data ? ` — ${historyQuery.data.title}` : ""}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2 rounded-xl border p-3">
              <Label htmlFor="version-file">Upload a new version</Label>
              <p className="text-xs text-muted-foreground">
                The current file is preserved and stays downloadable below.
              </p>
              <div className="flex items-center gap-2">
                <Input
                  id="version-file"
                  type="file"
                  onChange={(event) => setVersionFile(event.target.files?.[0] || null)}
                />
                <Button
                  onClick={() => versionMutation.mutate()}
                  disabled={!versionFile || versionMutation.isPending}
                  className="shrink-0 gap-2"
                >
                  {versionMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  Upload
                </Button>
              </div>
            </div>

            <div className="space-y-2">
              {historyQuery.isLoading ? (
                <p className="py-4 text-center text-sm text-muted-foreground">Loading history…</p>
              ) : (
                <>
                  {historyQuery.data ? (
                    <div className="flex items-center justify-between rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm">
                      <div>
                        <p className="font-medium">
                          v{historyQuery.data.version} (current) ·{" "}
                          {formatBytes(historyQuery.data.fileSizeBytes)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Updated {formatDate(historyQuery.data.updatedAt)}
                        </p>
                      </div>
                      {historyQuery.data.downloadURL ? (
                        <Button asChild size="sm" variant="outline">
                          <a href={historyQuery.data.downloadURL} target="_blank" rel="noreferrer">
                            Open
                          </a>
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                  {[...(historyQuery.data?.previousVersions || [])].reverse().map((version) => (
                    <div
                      key={`${version.version}-${version.uploadedAt}`}
                      className="flex items-center justify-between rounded-xl border p-3 text-sm"
                    >
                      <div>
                        <p className="font-medium">
                          v{version.version} · {formatBytes(version.fileSizeBytes)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Uploaded {formatDate(version.uploadedAt)}
                        </p>
                      </div>
                      {version.downloadURL ? (
                        <Button asChild size="sm" variant="outline">
                          <a href={version.downloadURL} target="_blank" rel="noreferrer">
                            Open
                          </a>
                        </Button>
                      ) : null}
                    </div>
                  ))}
                  {historyQuery.data && !(historyQuery.data.previousVersions || []).length ? (
                    <p className="py-2 text-center text-xs text-muted-foreground">
                      No earlier versions yet.
                    </p>
                  ) : null}
                </>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryDocumentId(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}
